import React, { ReactNode, useEffect, useState } from 'react';

import { PanelsEnum } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { isEmpty, map, sum } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getIconColor } from 'components/DataDisplay';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { PrimaryButton } from 'components/DataEntry';
import { CustomerJourneyBulbIcon } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { selectDashboard } from 'containers/Dashboard/selectors';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { renderResourcesWithIconAndCount } from 'containers/Dashboard/utils/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {
  label: ReactNode;
}

export const VulnerablePubliclyExposedEntities: React.FC<Props> = props => {
  const { vulnerablePubliclyAccessibleSources } = useSelector(selectDashboard);
  const navigate = useNavigate();
  const { isVulnerabilityScannerDeployed } = useSelector(selectSharedState);
  const { environmentId } = useAccessBoundary();
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setTotal(sum(map(vulnerablePubliclyAccessibleSources.data?.items, o => o)));
  }, [vulnerablePubliclyAccessibleSources.data]);

  return (
    <>
      <DashboardWidget
        isLoading={vulnerablePubliclyAccessibleSources.isLoading}
        label={props.label}
        noData={isEmpty(vulnerablePubliclyAccessibleSources.data?.items)}
        noDataMessage={renderNoEntitiesFoundWithIcon()}
        subHeader={total + ' entit' + (total === 1 ? 'y' : 'ies')}
        content={
          isVulnerabilityScannerDeployed?.[environmentId!] ? (
            renderResourcesWithIconAndCount(
              map(
                vulnerablePubliclyAccessibleSources.data?.items,
                (o, key) => ({
                  label: key,
                  iconType: key,
                  count: formatNumber(o),
                  iconColor: getIconColor(key as string),
                  onClick: () => {
                    allEntitiesDisclosure.onOpen();
                    setDetails({
                      panel: PanelsEnum.PubliclyExposedWithVulnerabilities,
                      iconType: key,
                      resourceType: key,
                      resourceTypeClass: 'agnostic',
                      total: o ?? 0,
                    });
                  },
                }),
              ),
            )
          ) : (
            <HStack
              spacing={4}
              align="flex-start"
              fontSize="md"
              justify="center"
              pb={8}
            >
              <Box boxSize={10} color="orange">
                <Center>
                  <CustomerJourneyBulbIcon />
                </Center>
              </Box>
              <Stack spacing={4} flex={1}>
                <Stack color="primary" cursor="pointer" spacing={0}>
                  <Box>Deploy Outpost for</Box>
                  <Box>Vulnerability for this insight</Box>
                </Stack>
                <Box>
                  <PrimaryButton
                    onClick={() => navigate('/setup/outpost/add/vulnerability')}
                  >
                    DEPLOY
                  </PrimaryButton>
                </Box>
              </Stack>
            </HStack>
          )
        }
        styles={{ contentWrapper: { px: 0, pb: 0 } }}
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        {...details}
      />
    </>
  );
};
